<template>
    <div>
        <div v-if="store.workOrderCode" class="mb-11 flex flex-wrap items-center gap-3">
            <div class="flex min-w-0 flex-1 items-center gap-3">
                <work-order-tag />
                <div class="min-w-0 truncate text-[#002377] underline">{{ store.workOrderCode }}</div>
            </div>
        </div>

        <step-progress :steps="steps" class="my-5" />

        <div v-if="!store.selectedFailureCode && !store.workOrderId">
            <h2 class="mb-0 !text-[22px] !font-bold">{{ trans('fieldresolvableissue.issue_triage.select_issues_heading') }}</h2>
            <p>{{ trans('fieldresolvableissue.issue_triage.error_message_notice') }}</p>
        </div>

        <tag-cloud :model-value="store.selectedFailureCode" :tags="failureCodes" class="my-8" @update:model-value="onFailureCodeSelected" />

        <div v-if="store.isKnownFailure">
            <div v-if="guidance">
                <p v-if="guidance?.description" class="text-lg font-medium">{{ guidance.description.translations?.en }}</p>

                <guidance-steps-list v-if="guidance?.steps?.length" :steps="guidance.steps" class="mt-4" />

                <h2 class="!mt-12 mb-0 !text-[22px] !font-bold">{{ trans('fieldresolvableissue.issue_triage.resolution_question') }}</h2>
            </div>
            <c-alert v-else-if="store.guidanceError" class="mb-0" color="danger">
                <div class="flex items-start gap-3">
                    <icon class="mt-0.5 shrink-0" icon="cil-warning" />
                    <div>
                        <strong>{{ trans('fieldresolvableissue.issue_triage.guidance_error_title') }}</strong>
                        <p class="mt-1 mb-0">{{ store.guidanceError }}</p>
                    </div>
                </div>
            </c-alert>
            <div v-else class="flex h-12 items-center justify-center">
                <spinner />
            </div>
        </div>

        <div v-else-if="store.isUnknownOrNotListedFailure">
            <p>{{ trans('fieldresolvableissue.issue_triage.detailed_description_prompt') }}</p>
            <row class="mt-3">
                <column>
                    <jet-textarea
                        v-model="form.additionalInformation"
                        :horizontal="false"
                        :invalidFeedback="form.errors.additionalInformation"
                        :maxlength="500"
                        :placeholder="trans('fieldresolvableissue.issue_triage.add_observations_placeholder')"
                        :rows="4"
                        name="observations"
                    />
                </column>
            </row>
            <row class="mt-5">
                <column>
                    <file-upload
                        v-model="form.attachments"
                        :class="{ 'is-invalid': form.errors.attachments }"
                        @upload-started="form.processing = true"
                        @upload-completed="form.processing = false"
                    />
                    <div class="invalid-feedback">
                        {{ form.errors.attachments }}
                    </div>
                </column>
            </row>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import StepProgress from '@/Components/StepProgress.vue';
import TagCloud from '@/Components/TagCloud.vue';
import GuidanceStepsList from '@/Components/GuidanceStepsList.vue';
import JetTextarea from '@/Jetstream/Textarea.vue';
import FileUpload from '@/Components/FileUpload.vue';
import Column from '@/Components/Column.vue';
import { useDiagnoseFieldIssueStore } from '@/stores/diagnoseFieldIssueStore';
import WorkOrderTag from '@/Components/CustomTags/WorkOrderTag.vue';
import { trans } from '@/mixins/i18n.js';

const store = useDiagnoseFieldIssueStore();

defineProps({ form: Object });

const steps = computed(() => store.processSteps ?? []);

const failureCodes = computed(() => {
    return (store.failureCodes || []).map((step) => ({
        ...step,
        title: step.title?.translations?.en ?? step.label ?? '',
    }));
});

const guidance = computed(() => store.guidance);

function onFailureCodeSelected(code) {
    if (code) {
        store.getIssueGuidance(code);
    } else {
        store.clearFailureCode();
    }
}
</script>
